import { useState } from 'react';
import {
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
  SelectProps,
  MenuItem,
  Checkbox,
  ListItemText,
} from '@mui/material';
import { Controller, Control } from 'react-hook-form';

type ControlledMultiSelectProps = SelectProps & {
  control: Control<any, any>;
  name: string;
  required?: boolean;
  errorMessage?: string;
  helperText?: string;
  options: Array<{ value: string | number; label: string }>;
};

export const ControlledMultiSelect = ({
  name,
  label,
  control,
  defaultValue = [],
  options,
  errorMessage,
  helperText,
  sx,
  ...props
}: ControlledMultiSelectProps) => {
  const [hasFocus, setHasFocus] = useState(false);
  const labelId = `${name}-label`;

  return (
    <Controller
      name={name}
      control={control}
      defaultValue={defaultValue}
      render={({ field: { onChange, onBlur, value = [], ref }, fieldState: { error } }) => (
        <FormControl
          error={Boolean(error)}
          sx={{
            width: props.fullWidth ? '100%' : 'auto',
            '& label': {
              top: !hasFocus && (!value || value.length === 0) ? '-8px' : 'initial',
            },
            ...sx,
          }}
        >
          <InputLabel id={labelId}>{label}</InputLabel>
          <Select
            labelId={labelId}
            label={label}
            multiple
            onChange={(event) => onChange(event.target.value)}
            onBlur={() => {
              setHasFocus(false);
              onBlur();
            }}
            onFocus={(event) => {
              setHasFocus(true);
              props?.onFocus?.(event);
            }}
            value={value}
            ref={ref}
            renderValue={(selected) =>
              options
                .filter((option) => selected.includes(option.value))
                .map((option) => option.label)
                .join(', ')
            }
            {...props}
          >
            {options.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                <Checkbox checked={value.indexOf(option.value) > -1} />
                <ListItemText primary={option.label} />
              </MenuItem>
            ))}
          </Select>
          {error && errorMessage && (
            <FormHelperText id={`component-error-text-${name}`}>{errorMessage}</FormHelperText>
          )}
          {helperText && !(error && errorMessage) && (
            <FormHelperText id={`component-error-text-${name}`}>{helperText}</FormHelperText>
          )}
        </FormControl>
      )}
    />
  );
};
